import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { useAuth } from '../../context/AuthContext';
import { useChat } from '../../context/ChatContext';
import ChatWindow from '../Chat/ChatWindow';

const ChatPage = () => {
  const { t } = useTranslation();
  const { chatId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const { chats = [] } = useChat();
  const [otherUser, setOtherUser] = useState(null);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  // Determine where to navigate back to
  const getBackPath = () => {
    // If coming from store management, go back to store management chat tab
    if (location.state?.fromStoreManagement) {
      return '/marketplace/my-store?tab=chat';
    }
    // Otherwise, go to marketplace home
    return '/marketplace';
  };

  const handleBack = () => {
    navigate(getBackPath());
  };

  useEffect(() => {
    console.log('ChatPage useEffect - chatId:', chatId, 'currentUser:', currentUser?.uid);
    
    if (!chatId) {
      console.error('ChatPage: No chatId provided');
      setLoading(false);
      return;
    }
    
    if (!currentUser) {
      console.error('ChatPage: No currentUser');
      setLoading(false);
      return;
    }

    // Try to fetch chat from Firestore
    fetchChatInfo();
  }, [chatId, currentUser]);

  const fetchChatInfo = async () => {
    if (!chatId) {
      console.error('fetchChatInfo: chatId is missing');
      setLoading(false);
      return;
    }
    
    try {
      console.log('Fetching chat with ID:', chatId);
      const chatRef = doc(db, 'chats', chatId);
      const chatSnap = await getDoc(chatRef);
      
      if (chatSnap.exists()) {
        const chatData = chatSnap.data();
        console.log('Chat data loaded:', chatData);
        const otherUserId = chatData.participants?.find(id => id !== currentUser.uid);
        
        if (otherUserId) {
          await fetchUserInfo(otherUserId, { id: chatSnap.id, ...chatData });
          
          // Load product info if it's a product inquiry
          if (chatData.type === 'product_inquiry' && chatData.productId) {
            await fetchProductInfo(chatData.productId);
          }
        } else {
          console.warn('No otherUserId found in chat participants');
          setLoading(false);
        }
      } else {
        console.error('Chat document does not exist:', chatId);
        setLoading(false);
      }
    } catch (error) {
      console.error('Error fetching chat:', error);
      setLoading(false);
    }
  };

  const fetchProductInfo = async (productId) => {
    try {
      const productRef = doc(db, 'products', productId);
      const productSnap = await getDoc(productRef);
      if (productSnap.exists()) {
        setProduct({ id: productSnap.id, ...productSnap.data() });
      }
    } catch (error) {
      console.error('Error fetching product info:', error);
    }
  };

  const fetchUserInfo = async (otherUserId, chat) => {
    try {
      // Try to get user info
      const userRef = doc(db, 'users', otherUserId);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const userData = userSnap.data();
        setOtherUser({
          id: otherUserId,
          name: userData.displayName || userData.name || t('chat.user'),
          ...userData
        });
      } else {
        // If no user doc, try to get store info if it's a product inquiry
        if (chat.type === 'product_inquiry' && chat.storeId) {
          const storeRef = doc(db, 'stores', chat.storeId);
          const storeSnap = await getDoc(storeRef);
          
          if (storeSnap.exists()) {
            const storeData = storeSnap.data();
            setOtherUser({
              id: otherUserId,
              name: storeData.name || t('chat.store'),
              isStore: true
            });
          } else {
            setOtherUser({
              id: otherUserId,
              name: t('chat.user')
            });
          }
        } else {
          setOtherUser({
            id: otherUserId,
            name: t('chat.user')
          });
        }
      }
    } catch (error) {
      console.error('Error fetching user info:', error);
      setOtherUser({
        id: otherUserId,
        name: t('chat.user')
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('chat.loading')}</p>
        </div>
      </div>
    );
  }

  if (!chatId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <p className="text-red-500 text-lg mb-4">{t('chat.chatNotFound')}</p>
          <p className="text-gray-600 text-sm mb-4">{t('chat.pleaseSelectChat')}</p>
          <button
            onClick={handleBack}
            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
          >
            {t('common.back')}
          </button>
        </div>
      </div>
    );
  }

  if (!otherUser) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <p className="text-red-500 text-lg mb-4">{t('chat.userNotFound')}</p>
          <p className="text-gray-600 text-sm mb-4">{t('chat.cannotLoadChat')}</p>
          <button
            onClick={handleBack}
            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
          >
            {t('common.back')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with Back Button and Breadcrumb */}
      <div className="bg-white shadow-md sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={handleBack}
              className="text-gray-600 hover:text-gray-800 transition-colors flex items-center gap-1"
              title={t('common.back')}
            >
              <span className="text-2xl font-bold">&lt;&lt;</span>
            </button>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <span>{t('chat.myStore')}</span>
              <span>/</span>
              <span>{t('chat.chat')}</span>
              <span>/</span>
              <span className="text-gray-800 font-medium">{otherUser.name}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-4">
        <div className="bg-white rounded-lg shadow-lg h-[calc(100vh-12rem)] flex flex-col">
          <ChatWindow
            chatId={chatId}
            otherUserId={otherUser.id}
            otherUserName={otherUser.name}
            productInfo={product}
            onClose={handleBack}
          />
        </div>
      </div>
    </div>
  );
};

export default ChatPage;

